import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import {
  Box, Button, Chip, CircularProgress, IconButton,
  InputAdornment, SwipeableDrawer, TextField,
  Tooltip, Typography, useMediaQuery, Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { withSnackbar } from '../../utils/SnackbarHOC';
import MultiCritere from './Accordions/MultiCritere';
import ButtonList from './Controls/ButtonList';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom';
import { updateObjectStateValue } from '../../utils/FunctionHelpers';
import {
  getAllSearchLists,
  resolveCandidatesForIa,
  agentTextSearch,
} from '../../services/SearchService';
import { TABLE } from '../../enums/tablesEnum';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

const SearchWrapper = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  borderRadius: 16,
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
  padding: theme.spacing(2.5, 3),
  marginBottom: theme.spacing(2),
  transition: 'box-shadow 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    // Filter + Operations share the second line equally on phones.
    '& .MuiButton-root': { flex: '1 1 0', justifyContent: 'center' },
  },
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
}));

const CategoryTab = styled(Box)(({ active, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 16px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: active ? 600 : 400,
  fontSize: '1rem',
  transition: 'all 0.18s ease',
  userSelect: 'none',
  background: active ? 'linear-gradient(135deg, #0556a4, #03407b)' : 'transparent',
  color: active ? '#fff' : '#64748b',
  border: active ? 'none' : '1px solid transparent',
  '&:hover': {
    background: active ? 'linear-gradient(135deg, #03407b, #022d68)' : '#f1f5f9',
    color: active ? '#fff' : '#334155',
  },
}));

const ModernSearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#f8fafc',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover': {
      backgroundColor: '#f1f5f9',
      '& fieldset': { borderColor: '#03407b' },
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: '0 0 0 3px rgba(99,102,241,0.1)',
      '& fieldset': { borderColor: '#03407b', borderWidth: 2 },
    },
  },
}));

const FilterButton = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1rem',
  border: '1px solid #e2e8f0',
  color: '#475569',
  background: '#fff',
  transition: 'all 0.18s ease',
  '&:hover': {
    background: '#f8fafc',
    borderColor: '#03407b',
    color: '#03407b',
  },
}));

const categories = [
  { value: TABLE.CANDIDAT, label: 'Candidats', icon: <PeopleAltOutlinedIcon sx={{ fontSize: 16 }} /> },
  { value: TABLE.CONTACT, label: 'Contacts', icon: <PersonOutlinedIcon sx={{ fontSize: 16 }} /> },
  { value: TABLE.SOCIETE, label: 'Sociétés', icon: <BusinessOutlinedIcon sx={{ fontSize: 16 }} /> },
];

const Search = ({
  candidatsData,
  societesData,
  contactsData,
  handleSearch,
  snackbarShowMessage,
  onIaActivate,
  onIaDeactivate,
  onImportCandidats,
}) => {
  const history = useHistory();
  const location = useLocation();
  const restoredAgentQueryRef = useRef('');

  const getInitialIaForm = () => {
    const loc = window.location.search;
    if (loc) {
      const searchParams = new URLSearchParams(loc);
      const isAiSearch = searchParams.get('isAiSearch') === 'true';
      if (isAiSearch) {
        const getArrayParam = (key) => {
          const bracketValues = searchParams.getAll(`${key}[]`);
          if (bracketValues.length > 0) return bracketValues;
          const singleValue = searchParams.get(key);
          return singleValue ? singleValue.split(',').filter(Boolean) : [];
        };
        return {
          loc: getArrayParam('loc'),
          exclude: searchParams.get('exclude') || '',
          skills: getArrayParam('skills'),
          programming_languages: getArrayParam('programming_languages'),
          role: searchParams.get('role') || '',
          experience: {
            min: searchParams.get('experience_min') ? Number(searchParams.get('experience_min')) : undefined,
            max: searchParams.get('experience_max') ? Number(searchParams.get('experience_max')) : undefined,
          },
          industry: getArrayParam('industry'),
        };
      }
    }
    return {};
  };

  const getInitialParameters = () => {
    const loc = window.location.search;
    if (loc) {
      const searchParams = new URLSearchParams(loc);
      const params = {};
      for (const [key, value] of searchParams.entries()) {
        if (['searchKeys', 'selection', 'societe', 'searchMode', 'nomFamille',
          'isAiSearch', 'loc', 'exclude', 'skills', 'programming_languages', 'role',
          'experience_min', 'experience_max', 'industry', 'iaMode', 'agentText'].includes(key)) continue;
        params[key] = value;
      }
      const iaForm = getInitialIaForm();
      if (Object.keys(iaForm).length > 0) params.iaForm = iaForm;
      return params;
    }
    return { prenom: '', categorie: TABLE.CANDIDAT, motsCleCategorie: 'fonction' };
  };

  const getInitialSearchKeys = () => {
    const loc = window.location.search;
    if (loc) {
      const searchParams = new URLSearchParams(loc);
      return {
        motsCle: searchParams.get('searchKeys') ? searchParams.get('searchKeys').split('|') : [],
        selection: searchParams.get('selection') || '',
        societe: searchParams.get('societe') ? searchParams.get('societe').split(',') : [],
      };
    }
    return { motsCle: [], societe: [], selection: '' };
  };

  const getInitialSearchMode = () => {
    const loc = window.location.search;
    if (loc) {
      const searchParams = new URLSearchParams(loc);
      return searchParams.get('searchMode') || 'or';
    }
    return 'or';
  };

  const getInitialNomFamille = () => {
    const loc = window.location.search;
    if (loc) {
      const searchParams = new URLSearchParams(loc);
      return searchParams.get('nomFamille') === 'true';
    }
    return false;
  };

  const [parameters, setParameters] = useState(getInitialParameters());
  const [searchKeys, setSearchKeys] = useState(getInitialSearchKeys());
  const [searchMode, setSearchMode] = useState(getInitialSearchMode());
  const [nomFamille, setNomFamille] = useState(getInitialNomFamille());
  const [motsCleCategorie, setMotsCleCategorie] = useState('tous');
  const [multiOpen, setMultiOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectionsData, setSelectionsData] = useState({
    societes: { data: [] },
    selections: { data: [] },
    competenceMetier: [],
    competenceSecto: [],
    consultants: [],
    managers: [],
  });

  const getUserData = () => JSON.parse(localStorage.getItem('userData'));
  const connectedUser = getUserData();
  const connectedUserId = connectedUser?.uuid || connectedUser?.id || connectedUser?.userId || '';
  const userRole = connectedUser?.role?.[0];

  const addParam = useCallback((e) => {
    const { name, value } = e.target;
    setParameters((prev) => ({ ...prev, [name]: value || '' }));
  }, []);

  const settingUpListsForAutoComplete = (idKeyName, valueKeyName, data) =>
    data?.map(({ [idKeyName]: id, [valueKeyName]: value, ...rest }) => ({ id, value, ...rest }));

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      history.push(`/accueil/chercher?${new URLSearchParams(parameters).toString()}`);
    }
  };

  useEffect(() => {
    const updatedParams = {
      ...parameters,
      searchKeys: searchKeys?.motsCle?.length ? searchKeys.motsCle.join('|') : '',
      searchMode,
      nomFamille: nomFamille.toString(),
    };
    if (searchKeys?.societe?.length > 0 && Array.isArray(searchKeys.societe)) {
      updatedParams.societe = searchKeys.societe.map((s) => s.id).join(',');
    }
    if (searchKeys?.selection?.id) {
      updatedParams.selection = searchKeys.selection.id;
    }
    setParameters(updatedParams);
  }, [searchKeys.motsCle, searchKeys.societe, searchKeys.selection, searchMode, nomFamille]);

  useEffect(() => {
    if (multiOpen) {
      let active = true;
      setLoading(true);
      (async () => {
        try {
          const { data } = await getAllSearchLists();
          if (!active) return;
          updateObjectStateValue(setSelectionsData, {
            societes: {
              data: settingUpListsForAutoComplete('uuid', 'nom_societe', data.societes),
              isLoading: loading,
              label: 'SOCIÉTÉ',
            },
            selections: { data: data.selections, isLoading: loading, label: 'SÉLECTIONS' },
            consultants: data.consultants,
            managers: data.managers,
            competenceSecto: data.competencesSectorielles,
            competenceMetier: data.competencesMetiers,
          });
        } catch {
          snackbarShowMessage('Erreur de chargement des listes.', 'error', 4000);
        } finally {
          setLoading(false);
        }
      })();
      return () => { active = false; };
    }
  }, [multiOpen]);

  useEffect(() => {
    if (!loading && selectionsData.selections.data.length > 0) {
      if (typeof searchKeys.selection === 'string' && searchKeys.selection) {
        const selectionObj = selectionsData.selections.data.find((s) => s.id === searchKeys.selection);
        if (selectionObj) updateObjectStateValue(setSearchKeys, { selection: selectionObj });
      }
      if (Array.isArray(searchKeys.societe) && searchKeys.societe.length > 0 && typeof searchKeys.societe[0] === 'string') {
        const societeObjs = searchKeys.societe
          .map((uuid) => selectionsData.societes.data.find((s) => s.id === uuid))
          .filter(Boolean);
        if (societeObjs.length > 0) updateObjectStateValue(setSearchKeys, { societe: societeObjs });
      }
    }
  }, [loading, selectionsData.selections.data, selectionsData.societes.data]);

  const handleNameChecked = (e) => {
    const { checked } = e.target;
    const paramName = checked ? 'prenom' : 'nom';
    const otherParamName = checked ? 'nom' : 'prenom';
    const { [paramName]: temp, ...newParameters } = parameters;
    setParameters({ ...newParameters, [otherParamName]: temp });
    setNomFamille(checked);
  };

  const handleSearchModeChange = (event) => setSearchMode(event.target.value);

  const extractCandidatesFromIaResolve = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.candidats)) return payload.candidats;
    if (Array.isArray(payload?.candidates)) return payload.candidates;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  };

  const resolveCandidateIdFromRow = (row = {}) =>
    row.id ?? row.uuid ?? row.candidatId ?? row.candidat_id ?? row.candidatUuid ?? row.candidat_uuid ?? null;

  const getScoreForResolvedCandidate = (candidate = {}, scoreMap = new Map()) => {
    const uploadedFiles = Array.isArray(candidate.uploadedFiles) ? candidate.uploadedFiles : [];
    const idsToTry = [
      resolveCandidateIdFromRow(candidate),
      ...uploadedFiles.map((file) => file?.id ?? file?.uuid ?? null),
    ].filter(Boolean);
    for (const key of idsToTry) {
      const score = scoreMap.get(String(key));
      if (score != null) return score;
    }
    return null;
  };

  const handleAgentSearch = useCallback(async (text) => {
    if (!text?.trim()) return;
    onIaActivate?.({ data: [], isLoading: true });
    setMultiOpen(false);
    try {
      const res = await agentTextSearch(text, connectedUserId);
      const agentCandidates = Array.isArray(res.data?.Candidates) ? res.data.Candidates : [];
      if (agentCandidates.length === 0) {
        onIaActivate?.({ data: [], isLoading: false });
        return;
      }
      const fallbackCandidates = agentCandidates
        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
        .map((c) => {
          const p = c.properties || {};
          const nameParts = (p.full_name || '').trim().split(/\s+/).filter(Boolean);
          const id = resolveCandidateIdFromRow(c) ?? c.id;
          return {
            ...c,
            candidat_uuid: c.candidat_uuid ?? id,
            id,
            ia_score: c.ia_score ?? c.score ?? null,
            nom: c.nom || p.last_name || nameParts.slice(1).join(' ') || '',
            prenom: c.prenom || p.first_name || nameParts[0] || '',
            email: c.email || (p.email !== 'not found' ? p.email : ''),
            telephone: c.telephone || (p.phone !== 'not found' ? p.phone : ''),
            fonction: c.fonction || p.roles_held?.[0] || '',
            uploadedFiles: Array.isArray(c.uploadedFiles) ? c.uploadedFiles : [],
          };
        });

      let candidates = [];
      const sortedAgentCandidates = [...agentCandidates].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
      const scoreByAgentId = new Map(
        sortedAgentCandidates.filter((c) => c?.id).map((c) => [String(c.id), c.score ?? 0])
      );
      const agentIds = sortedAgentCandidates.map((c) => c?.id).filter(Boolean);

      if (agentIds.length > 0) {
        try {
          const resolved = await resolveCandidatesForIa(agentIds);
          const resolvedRows = extractCandidatesFromIaResolve(resolved?.data);
          const normalizedRows = resolvedRows
            .filter(Boolean)
            .map((row) => {
              const id = resolveCandidateIdFromRow(row);
              return {
                ...row,
                id,
                candidat_uuid: row.candidat_uuid ?? id,
                ia_score: getScoreForResolvedCandidate(row, scoreByAgentId),
                uploadedFiles: Array.isArray(row.uploadedFiles) ? row.uploadedFiles : [],
              };
            })
            .sort((a, b) => (b.ia_score ?? -1) - (a.ia_score ?? -1));
          const seenIds = new Set();
          candidates = normalizedRows.filter((row) => {
            const key = row?.id ? String(row.id) : null;
            if (!key || seenIds.has(key)) return false;
            seenIds.add(key);
            return true;
          });
        } catch {
          candidates = [];
        }
      }

      if (candidates.length === 0) candidates = fallbackCandidates;
      onIaActivate?.({ data: candidates, isLoading: false });
    } catch {
      snackbarShowMessage('Erreur de recherche par agent IA.', 'error', 4000);
      onIaActivate?.({ data: [], isLoading: false });
    }
  }, [onIaActivate, snackbarShowMessage, connectedUserId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isAiSearch = searchParams.get('isAiSearch') === 'true';
    const iaMode = searchParams.get('iaMode');
    const agentText = (searchParams.get('agentText') || '').trim();
    if (!(isAiSearch && iaMode === 'agent' && agentText)) {
      restoredAgentQueryRef.current = '';
      return;
    }
    if (restoredAgentQueryRef.current === agentText) return;
    restoredAgentQueryRef.current = agentText;
    handleAgentSearch(agentText);
  }, [location.search, handleAgentSearch]);

  const handleCategoryChange = (cat) => {
    const newParams = { ...parameters, categorie: cat };
    setParameters(newParams);
    handleSearch(newParams);
  };

  const multiCritereProps = {
    classes: {},
    motsCleCategorie,
    setMotsCleCategorie,
    multiOpen,
    setMultiOpen,
    addParam,
    parameters,
    searchKeys,
    setSearchKeys,
    loading,
    selectionsData,
    handleSearch,
    handleNameChecked,
    searchMode,
    handleSearchModeChange,
    nomFamille,
    onAgentSearch: handleAgentSearch,
  };

  const isSmall = useMediaQuery((theme) => theme.breakpoints.between(0, 767));
  const isMediumMode = useMediaQuery((theme) => theme.breakpoints.between(767, 1023));

  const selectedCandidats = useSelector((s) => s.candidats.candidatsSelectedForSelection);

  const currentCategory = parameters?.categorie || TABLE.CANDIDAT;

  const resultCount =
    currentCategory === TABLE.CANDIDAT ? candidatsData.candidatsCount :
    currentCategory === TABLE.CONTACT ? contactsData.contactsCount :
    societesData.societesCount;

  const hasResults =
    candidatsData.loadCandidats || societesData.loadSociete || contactsData.loadContacts;

  const allowedCategories = userRole === 'ROLE_TESTEUR'
    ? categories.filter((c) => c.value === TABLE.CANDIDAT)
    : categories;

  return (
    <Box sx={{ px: { xs: 1, md: 0 } }}>
      <SearchWrapper>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {allowedCategories.map((cat) => (
              <CategoryTab
                key={cat.value}
                active={currentCategory === cat.value ? 1 : 0}
                onClick={() => handleCategoryChange(cat.value)}
              >
                {cat.icon}
                {cat.label}
              </CategoryTab>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasResults && resultCount > 0 && (
              <Chip
                label={`${resultCount.toLocaleString()} résultat${resultCount > 1 ? 's' : ''}`}
                size="small"
                sx={{
                  background: '#ebf4fb',
                  color: '#03407b',
                  fontWeight: 600,
                  fontSize: '1rem',
                  border: '1px solid #aed6f1',
                }}
              />
            )}
            {onImportCandidats && currentCategory === TABLE.CANDIDAT && (
              <Button
                onClick={onImportCandidats}
                startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  borderRadius: '10px',
                  borderColor: '#03407b',
                  color: '#03407b',
                  px: 2,
                  py: 0.75,
                  fontFamily: "'Fira Sans', sans-serif",
                  '&:hover': { background: '#ebf4fb', borderColor: '#03407b' },
                }}
              >
                Importer des candidats
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          <ModernSearchField
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }}
            {...(nomFamille ? { name: 'nom' } : { name: 'prenom' })}
            value={parameters?.nom ?? parameters?.prenom ?? ''}
            onChange={addParam}
            variant="outlined"
            autoComplete="off"
            fullWidth
            placeholder={
              currentCategory === TABLE.CANDIDAT
                ? 'Nom, prénom, N° dossier, téléphone ou lien LinkedIn…'
                : currentCategory === TABLE.CONTACT
                ? 'Nom, prénom ou email…'
                : 'Nom de la société…'
            }
            onKeyDown={handleKeyPress} slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Rechercher">
                    <IconButton
                      onClick={() => history.push(`/accueil/chercher?${new URLSearchParams(parameters).toString()}`)}
                      size="small"
                      sx={{ color: '#03407b', '&:hover': { background: '#ebf4fb' } }}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            } }}
          />

          <FilterButton
            startIcon={<TuneOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={() => setMultiOpen(true)}
          >
            Filtrer
          </FilterButton>

          <ButtonList
            loading={loading}
            selectedCandidats={selectedCandidats}
            history={history}
          />
        </Box>
      </SearchWrapper>

      <SwipeableDrawer
        anchor="right"
        open={multiOpen}
        onClose={() => setMultiOpen(false)}
        onOpen={() => setMultiOpen(true)}
      >
        <Box sx={{ width: isSmall ? '100vw' : isMediumMode ? '60vw' : '35vw', maxWidth: '100vw' }}>
          <MultiCritere {...multiCritereProps} />
        </Box>
      </SwipeableDrawer>
    </Box>
  );
};

export default withSnackbar(Search);
